import React, { useEffect, useRef, useState } from 'react';
import { Box, Dialog, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { toast } from 'react-toastify';
import { API } from '../../../../../api';
import useEditingFrozen from '../../../../../hooks/useEditingFrozen';
import { EDITING_FROZEN_MESSAGE } from '../../../../../utils/constants';
import type { ElliottSections, ElliottSimulationRubric } from '../../../../../pages/new-designs/tabs/aimd-v2/types';
import LiveSimulationSection from '../../../../../pages/new-designs/tabs/aimd-v2/components/LiveSimulationSection';
import ActionPill from '../../../../../pages/new-designs/tabs/aimd-v2/components/ActionPill';
import ImproveWithAIModal from '../../../../../pages/new-designs/tabs/aimd-v2/components/ImproveWithAIModal';
import SectionToggle from '../../../../../pages/new-designs/tabs/aimd-v2/components/SectionToggle';

type ElliottSectionKey = 'caseStudies' | 'simulation';

type CaseStudy = { id: string; title: string; sections: { label: string; text: string }[] };

const BLANK_CASE_STUDY_SECTIONS = [
  { label: 'Scenario', text: '' },
  { label: 'Action', text: '' },
  { label: 'Relevance', text: '' }
];

// Same outlined container as the aimd-v2 stage-6 editor sections.
const sectionBoxSx = {
  border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
  borderRadius: '12px',
  background: 'rgba(255,255,255,0.02)',
  p: 2.5
} as const;

const sectionTitleSx = {
  fontSize: 15,
  fontWeight: 600,
  fontFamily: 'Inter',
  color: 'var(--Text-text-tertiary, #868686)'
} as const;

/** Empty state for a section that was never generated (org/program not backfilled). */
const GenerateEmptyState: React.FC<{
  description: string;
  loading: boolean;
  onGenerate: () => void;
}> = ({ description, loading, onGenerate }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1.5 }}>
    <Typography sx={{ fontSize: 13, fontFamily: 'Inter', color: 'var(--Text-text-secondary, #B6B6B6)', lineHeight: '20px' }}>
      {description}
    </Typography>
    <ActionPill
      icon={<AutoAwesomeIcon sx={{ fontSize: 14, color: 'var(--Stroke-highlight, #F79420)' }} />}
      label={loading ? 'Generating...' : 'Generate with Elliott'}
      onClick={loading ? () => {} : onGenerate}
      disabled={loading}
    />
  </Box>
);

interface ElliottSectionsPanelProps {
  grpId: string;
  orgId: string;
  /** Live template ids (level._id / task._id) — the panel is only rendered when both exist. */
  levelId: string;
  taskId: string;
  elliott: Partial<ElliottSections> | null | undefined;
  /** Keeps the builder's lesson state in sync after a save/improve on this panel. */
  onElliottChange: (next: Partial<ElliottSections>) => void;
}

/**
 * Elliott learner-experience sections (Case Studies · Live Simulation · Quiz
 * improve) for the published-program designer. Mirrors the aimd-v2 stage-6
 * editor, but persists through the task-scoped elliott-section endpoint since
 * manually-built / backfilled programs have no generation artifact. Edits save
 * immediately (independent of the designer's "Save Changes").
 */
const ElliottSectionsPanel: React.FC<ElliottSectionsPanelProps> = ({
  grpId,
  orgId,
  levelId,
  taskId,
  elliott,
  onElliottChange
}) => {
  // Relaunch editing freeze (B4-2): edits here save immediately, so every
  // write handler bails before touching the network.
  const editingFrozen = useEditingFrozen();
  const caseStudies: CaseStudy[] = (elliott?.caseStudies || []).map((cs, i) => ({
    id: cs.id || `cs-${i}`,
    title: cs.title,
    sections: (cs.sections || []).map((s) => ({ label: s.label, text: s.text }))
  }));
  // Manual authoring from scratch: "Write manually" seeds a blank rubric so the
  // fields render editable even when Elliott never generated a simulation.
  const [draftRubric, setDraftRubric] = useState<ElliottSimulationRubric | null>(null);
  const simRubric: ElliottSimulationRubric | null = elliott?.simulationRubric ?? draftRubric;
  // On/off toggles — optimistic local values until the saved elliott round-trips.
  const [simEnabledOverride, setSimEnabledOverride] = useState<boolean | null>(null);
  const [csEnabledOverride, setCsEnabledOverride] = useState<boolean | null>(null);
  useEffect(() => {
    setSimEnabledOverride(null);
    setCsEnabledOverride(null);
  }, [elliott]);
  const simEnabled = simEnabledOverride ?? elliott?.simulationEnabled ?? true;
  const csEnabled = csEnabledOverride ?? elliott?.caseStudiesEnabled ?? true;

  const [expandedCaseStudy, setExpandedCaseStudy] = useState<number | null>(0);
  const [csEdit, setCsEdit] = useState<{
    mode: 'edit' | 'add';
    index: number;
    title: string;
    sections: { label: string; text: string }[];
  } | null>(null);
  const [improveTarget, setImproveTarget] = useState<ElliottSectionKey | null>(null);
  const [improveLoading, setImproveLoading] = useState(false);
  // Section currently running a feedback-less "Generate with AI" from an empty state.
  const [generating, setGenerating] = useState<ElliottSectionKey | null>(null);

  const persistSection = async (payload: {
    caseStudies?: CaseStudy[];
    simulationRubric?: { brief: string; skill: string; strongPoints: string[]; weakPoints: string[] };
    simulationEnabled?: boolean;
    caseStudiesEnabled?: boolean;
  }) => {
    if (editingFrozen) {
      toast.warn(EDITING_FROZEN_MESSAGE);
      return;
    }
    // Rubric typing and the on/off toggles sync the builder OPTIMISTICALLY: the
    // server only echoes what we sent, and applying that echo after the round
    // trip clobbers keystrokes typed while the request was in flight.
    const optimistic =
      Boolean(payload.simulationRubric) ||
      payload.simulationEnabled !== undefined ||
      payload.caseStudiesEnabled !== undefined;
    if (optimistic) {
      onElliottChange({
        ...(elliott || {}),
        ...(payload.simulationRubric
          ? {
              simulationRubric: {
                ...payload.simulationRubric,
                maxAttempts: elliott?.simulationRubric?.maxAttempts ?? 5
              }
            }
          : {}),
        ...(payload.simulationEnabled !== undefined ? { simulationEnabled: payload.simulationEnabled } : {}),
        ...(payload.caseStudiesEnabled !== undefined ? { caseStudiesEnabled: payload.caseStudiesEnabled } : {})
      } as Partial<ElliottSections>);
    }
    try {
      const res = await API.updateElliottSectionOnTask({ grpId, levelId, taskId, orgId, ...payload });
      const next: Partial<ElliottSections> | undefined = res.data?.data?.elliott ?? res.data?.elliott;
      if (next && !optimistic) onElliottChange(next);
    } catch {
      toast.error('Section changes could not be saved. Try again.');
    }
  };

  const handleSimToggle = (enabled: boolean) => {
    if (editingFrozen) {
      toast.warn(EDITING_FROZEN_MESSAGE);
      return;
    }
    setSimEnabledOverride(enabled);
    persistSection({ simulationEnabled: enabled });
  };

  const handleCsToggle = (enabled: boolean) => {
    if (editingFrozen) {
      toast.warn(EDITING_FROZEN_MESSAGE);
      return;
    }
    setCsEnabledOverride(enabled);
    persistSection({ caseStudiesEnabled: enabled });
  };

  // Rubric typing is debounced like the stage-6 editor; flush on unmount.
  const rubricSaveTimer = useRef<number | null>(null);
  const pendingRubricRef = useRef<{ brief: string; skill: string; strongPoints: string[]; weakPoints: string[] } | null>(null);
  const handleRubricChange = (rubric: { brief: string; skill: string; strongPoints: string[]; weakPoints: string[] }) => {
    pendingRubricRef.current = rubric;
    if (rubricSaveTimer.current) window.clearTimeout(rubricSaveTimer.current);
    rubricSaveTimer.current = window.setTimeout(() => {
      const pending = pendingRubricRef.current;
      pendingRubricRef.current = null;
      if (pending) persistSection({ simulationRubric: pending });
    }, 800);
  };
  useEffect(
    () => () => {
      if (rubricSaveTimer.current) window.clearTimeout(rubricSaveTimer.current);
      const pending = pendingRubricRef.current;
      pendingRubricRef.current = null;
      if (pending) persistSection({ simulationRubric: pending });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [grpId, levelId, taskId]
  );

  const runImprove = async (section: ElliottSectionKey, feedback: string) => {
    const res = await API.improveElliottSectionOnTask({ grpId, levelId, taskId, orgId, section, feedback });
    const next: Partial<ElliottSections> | undefined = res.data?.data?.elliott ?? res.data?.elliott;
    if (next) onElliottChange(next);
  };

  const handleImproveSubmit = async (feedback: string) => {
    if (editingFrozen) {
      toast.warn(EDITING_FROZEN_MESSAGE);
      return;
    }
    if (!improveTarget) return;
    setImproveLoading(true);
    try {
      await runImprove(improveTarget, feedback);
      toast.success('Section improved');
      setImproveTarget(null);
    } catch (err: any) {
      const message = err?.response?.data?.message;
      toast.error(message || 'Elliott could not improve this section. Try again.');
    } finally {
      setImproveLoading(false);
    }
  };

  const handleGenerate = async (section: ElliottSectionKey) => {
    if (editingFrozen) {
      toast.warn(EDITING_FROZEN_MESSAGE);
      return;
    }
    setGenerating(section);
    try {
      await runImprove(section, '');
      toast.success('Section generated');
    } catch (err: any) {
      const message = err?.response?.data?.message;
      toast.error(message || 'Elliott could not write this section. Try again.');
    } finally {
      setGenerating(null);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Case Studies */}
      <Box sx={sectionBoxSx}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1, minWidth: 240 }}>
            <Typography sx={sectionTitleSx}>Case Studies</Typography>
            <Typography sx={{ fontSize: 12, fontFamily: 'Inter', color: 'var(--Text-text-secondary, #B6B6B6)', lineHeight: 1.5 }}>
              Case Studies assess a learner&apos;s understanding by presenting a realistic scenario and outcome. They
              allow learners to see how the knowledge and principles covered in the lesson can be used in real-world
              situations.
            </Typography>
          </Box>
          <SectionToggle
            enabled={csEnabled}
            onToggle={handleCsToggle}
            title="Turn the Case Studies step on or off for this lesson"
          />
        </Box>
        {!csEnabled ? (
          <Typography sx={{ fontSize: 12, fontFamily: 'Inter', color: 'var(--Text-text-secondary, #B6B6B6)', lineHeight: 1.5, fontStyle: 'italic' }}>
            Case Studies are turned off for this lesson. Learners will move straight from the Lesson Body to the next
            step. Turn them back on to restore this step (your case studies are kept).
          </Typography>
        ) : caseStudies.length === 0 ? (
          <GenerateEmptyState
            description="Elliott hasn't written case studies for this lesson yet. Generate three case studies grounded in the Lesson Body, or add one manually below."
            loading={generating === 'caseStudies'}
            onGenerate={() => handleGenerate('caseStudies')}
          />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {caseStudies.map((cs, i) => {
              const open = expandedCaseStudy === i;
              return (
                <Box
                  key={cs.id}
                  sx={{
                    borderRadius: '8px',
                    px: 2,
                    py: 1.5,
                    background: 'linear-gradient(180deg, rgba(247,148,32,0.1) 0%, rgba(241,90,38,0.1) 100%)'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Box
                      onClick={() => setExpandedCaseStudy(open ? null : i)}
                      sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, cursor: 'pointer' }}
                    >
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: 700,
                          fontFamily: 'Inter',
                          color: 'var(--Text-text-primary, #FDFDFD)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {cs.title}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                      <ActionPill
                        icon={<AutoAwesomeIcon sx={{ fontSize: 14, color: 'var(--Stroke-highlight, #F79420)' }} />}
                        label="Improve with Elliott"
                        onClick={() => setImproveTarget('caseStudies')}
                      />
                      <ActionPill
                        icon={<EditIcon sx={{ fontSize: 14 }} />}
                        label="Edit Manually"
                        onClick={() =>
                          setCsEdit({
                            mode: 'edit',
                            index: i,
                            title: cs.title,
                            sections: cs.sections.map((s) => ({ ...s }))
                          })
                        }
                      />
                      <Box
                        onClick={() => setExpandedCaseStudy(open ? null : i)}
                        sx={{ display: 'flex', cursor: 'pointer', color: 'var(--Text-text-secondary, #B6B6B6)' }}
                      >
                        {open ? <ExpandLessIcon sx={{ fontSize: 20 }} /> : <ExpandMoreIcon sx={{ fontSize: 20 }} />}
                      </Box>
                    </Box>
                  </Box>
                  {open && (
                    <Box
                      sx={{
                        mt: 1,
                        pt: 1,
                        borderTop: '1px solid rgba(255,255,255,0.45)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5
                      }}
                    >
                      {cs.sections.map((s) => (
                        <Typography
                          key={s.label}
                          sx={{
                            fontSize: 14,
                            fontFamily: 'Inter',
                            color: 'var(--Text-text-primary, #FDFDFD)',
                            lineHeight: '24px',
                            whiteSpace: 'pre-wrap'
                          }}
                        >
                          <Box component="span" sx={{ fontWeight: 700 }}>
                            {s.label}:
                          </Box>{' '}
                          {s.text}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
        {csEnabled && (
          <Box
            onClick={() =>
              setCsEdit({
                mode: 'add',
                index: caseStudies.length,
                title: '',
                sections: BLANK_CASE_STUDY_SECTIONS.map((s) => ({ ...s }))
              })
            }
            sx={{
              mt: 1.5,
              borderRadius: '8px',
              border: '1px dashed var(--Stroke-Border-border-dim, #2A2A2A)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              py: 1.5,
              cursor: 'pointer',
              '&:hover': { borderColor: 'var(--Stroke-highlight, #F79420)' }
            }}
          >
            <AddIcon sx={{ fontSize: 18, color: 'var(--Text-text-tertiary, #868686)' }} />
            <Typography sx={{ fontSize: 12, fontFamily: 'Inter', color: 'var(--Text-text-tertiary, #868686)' }}>
              Add case study
            </Typography>
          </Box>
        )}
      </Box>

      {/* Live Simulation */}
      <Box sx={sectionBoxSx}>
        {simRubric ? (
          <LiveSimulationSection
            rubric={simRubric}
            onImprove={() => setImproveTarget('simulation')}
            onChange={handleRubricChange}
            enabled={simEnabled}
            onToggleEnabled={handleSimToggle}
          />
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1, minWidth: 240 }}>
                <Typography sx={sectionTitleSx}>Roleplay</Typography>
                <Typography sx={{ fontSize: 12, fontFamily: 'Inter', color: 'var(--Text-text-secondary, #B6B6B6)', lineHeight: 1.5 }}>
                  Roleplay assesses a learner&apos;s understanding by presenting a realistic situation that they must
                  must work through with Elliott. It is an effective way to verify that learners have not only absorbed
                  Lesson Body but can also apply their knowledge confidently in practical, real-world situations.
                </Typography>
              </Box>
              <SectionToggle
                enabled={simEnabled}
                onToggle={handleSimToggle}
                title="Turn the Scenario Testing step on or off for this lesson"
              />
            </Box>
            {simEnabled ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                <GenerateEmptyState
                  description="This lesson has no Roleplay yet. Generate a Roleplay brief and scoring rubric (strong / weak response points) from the Lesson Body."
                  loading={generating === 'simulation'}
                  onGenerate={() => handleGenerate('simulation')}
                />
                <Box
                  onClick={() =>
                    setDraftRubric({ brief: '', skill: '', strongPoints: [], weakPoints: [], maxAttempts: 5 })
                  }
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    cursor: 'pointer',
                    color: 'var(--Text-text-tertiary, #868686)',
                    '&:hover': { color: 'var(--Stroke-highlight, #F79420)' }
                  }}
                >
                  <EditIcon sx={{ fontSize: 13 }} />
                  <Typography sx={{ fontSize: 12, fontFamily: 'Inter' }}>…or write it manually</Typography>
                </Box>
              </Box>
            ) : (
              <Typography sx={{ fontSize: 12, fontFamily: 'Inter', color: 'var(--Text-text-secondary, #B6B6B6)', lineHeight: 1.5, fontStyle: 'italic' }}>
                Roleplay is turned off for this lesson. Learners will move straight from the case studies to the quiz.
              </Typography>
            )}
          </>
        )}
      </Box>

      {/* Case Study Edit/Add Modal — edits the title plus the Scenario/Action/Relevance text */}
      <Dialog
        open={!!csEdit}
        onClose={() => setCsEdit(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: '#1A1A1A',
            borderRadius: '12px',
            border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
            p: 3
          }
        }}
      >
        {csEdit && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Typography sx={{ fontSize: 18, fontWeight: 700, fontFamily: 'Inter', color: 'var(--Text-text-primary, #FDFDFD)' }}>
                {csEdit.mode === 'add' ? 'Add Case Study' : 'Edit Case Study'}
              </Typography>
              <Box
                component="button"
                onClick={() => setCsEdit(null)}
                sx={{ background: 'none', border: 'none', cursor: 'pointer', p: 0.5, flexShrink: 0 }}
              >
                <CloseIcon sx={{ color: 'var(--Text-text-secondary, #B6B6B6)', fontSize: 22 }} />
              </Box>
            </Box>

            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600, fontFamily: 'Inter', color: 'var(--Text-text-secondary, #B6B6B6)', mb: 1 }}>
                Title
              </Typography>
              <Box
                component="input"
                value={csEdit.title}
                placeholder={`Case Study ${csEdit.index + 1}`}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setCsEdit((prev) => (prev ? { ...prev, title: e.target.value } : prev))
                }
                sx={{
                  width: '100%',
                  background: 'transparent',
                  border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
                  borderRadius: '8px',
                  px: 1.5,
                  py: 1,
                  color: 'var(--Text-text-primary, #FDFDFD)',
                  fontFamily: 'Inter',
                  fontSize: 14,
                  lineHeight: '22px',
                  outline: 'none',
                  '&::placeholder': { color: 'var(--Text-text-tertiary, #868686)' },
                  '&:focus': { borderColor: 'var(--Stroke-highlight, #F79420)' }
                }}
              />
            </Box>

            {csEdit.sections.map((s, si) => (
              <Box key={s.label}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, fontFamily: 'Inter', color: 'var(--Text-text-secondary, #B6B6B6)', mb: 1 }}>
                  {s.label}
                </Typography>
                <Box
                  component="textarea"
                  value={s.text}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setCsEdit((prev) =>
                      prev
                        ? { ...prev, sections: prev.sections.map((x, xi) => (xi === si ? { ...x, text: e.target.value } : x)) }
                        : prev
                    )
                  }
                  rows={3}
                  sx={{
                    width: '100%',
                    background: 'transparent',
                    resize: 'vertical',
                    border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
                    borderRadius: '8px',
                    px: 1.5,
                    py: 1,
                    color: 'var(--Text-text-primary, #FDFDFD)',
                    fontFamily: 'Inter',
                    fontSize: 14,
                    lineHeight: '22px',
                    outline: 'none',
                    '&:focus': { borderColor: 'var(--Stroke-highlight, #F79420)' }
                  }}
                />
              </Box>
            ))}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 1 }}>
              <Box
                component="button"
                onClick={() => setCsEdit(null)}
                sx={{
                  background: '#1C1C1C',
                  border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
                  borderRadius: '8px',
                  px: 2.5,
                  py: 1,
                  cursor: 'pointer',
                  color: 'var(--Text-text-primary, #FDFDFD)',
                  fontFamily: 'Inter',
                  fontSize: 13,
                  fontWeight: 500,
                  '&:hover': { borderColor: 'var(--Stroke-highlight, #F79420)' }
                }}
              >
                Cancel
              </Box>
              <Box
                component="button"
                onClick={() => {
                  // Blank title falls back to the positional default so a card is never untitled.
                  const title = csEdit.title.trim() || `Case Study ${csEdit.index + 1}`;
                  const next =
                    csEdit.mode === 'add'
                      ? [...caseStudies, { id: `cs-${Date.now()}`, title, sections: csEdit.sections }]
                      : caseStudies.map((cs, i) =>
                          i === csEdit.index ? { ...cs, title, sections: csEdit.sections } : cs
                        );
                  persistSection({ caseStudies: next });
                  if (csEdit.mode === 'add') setExpandedCaseStudy(csEdit.index);
                  setCsEdit(null);
                }}
                sx={{
                  background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  px: 2.5,
                  py: 1,
                  cursor: 'pointer',
                  color: '#FDFDFD',
                  fontFamily: 'Inter',
                  fontSize: 13,
                  fontWeight: 600,
                  '&:hover': { opacity: 0.9 }
                }}
              >
                Save Changes
              </Box>
            </Box>
          </Box>
        )}
      </Dialog>

      {/* Section-scoped Improve with AI — same modal as stage-6, no regenerate banner */}
      <ImproveWithAIModal
        variant="section"
        open={improveTarget !== null}
        isLoading={improveLoading}
        onClose={() => {
          if (!improveLoading) setImproveTarget(null);
        }}
        onSubmit={(feedback) => handleImproveSubmit(feedback)}
      />
    </Box>
  );
};

export default ElliottSectionsPanel;
